import React, { useCallback, useEffect, useState } from "react";
import { StatusBar } from "./components/StatusBar";
import { Shell } from "./components/Shell";
import { MapView } from "./components/MapView";
import { GaugePanel } from "./components/GaugePanel";
import { RadioPanel } from "./components/RadioPanel";
import { RadioDrawer } from "./components/RadioDrawer";
import { CameraPanel } from "./components/CameraPanel";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FuelChip } from "./components/FuelChip";
import { AlertStrip } from "./components/AlertStrip";
import { AlertDrawer } from "./components/AlertDrawer";
import { ConnectionBanner } from "./components/ConnectionBanner";
import { SimBanner } from "./components/SimBanner";
import { MqttProvider } from "./contexts/MqttContext";
import { useActiveEvent, ActiveEventProvider } from "./contexts/ActiveEventContext";
import { RouterProvider, useRoute } from "./contexts/RouterContext";
import { TelemetryProvider, useTelemetryContext } from "./contexts/TelemetryContext";
import { LapTimerProvider, useLapTimerContext } from "./contexts/LapTimerContext";
import { AlertProvider, useAlertContext } from "./contexts/AlertContext";
import { ThemeProvider, useThemeContext } from "./contexts/ThemeContext";
import { API_BASE } from "./apiFetch";
import { useRadioCapture } from "./hooks/useRadioCapture";
import { useRadioTranscripts } from "./hooks/useRadioTranscripts";
import { useRefuels } from "./hooks/useRefuels";
import { effectiveLapCount } from "./lib/fuelProjection";
import { useFuelProjection } from "./hooks/useFuelProjection";
import { useRaceChronoLapTiming } from "./hooks/useRaceChronoLapTiming";
import { useSimMode } from "./hooks/useSimMode";
import { useStintClock, useStintTargetAlert } from "./hooks/useStintClock";
import { useConfig } from "./hooks/useConfig";
import { useDrivers } from "./hooks/useDrivers";
import { useSession } from "./hooks/useSession";
import { useTelemetrySampler } from "./hooks/useTelemetrySampler";
import { useTelemetryStaleness } from "./hooks/useTelemetryStaleness";
import { DEFAULT_TARGET_STINT_MINUTES } from "./config/gauges";
import "./styles/global.css";
import "./styles/theme-light.css";
import "./styles/components.css";

const CAMERA_STREAM_BASE =
  import.meta.env.VITE_CAMERA_STREAM_URL ??
  `${typeof location !== "undefined" && location.protocol === "https:" ? "wss:" : "ws:"}//${typeof location !== "undefined" ? location.host : ""}/camera/stream`;

/**
 * Inner content that consumes telemetry, lap timer, and alert contexts.
 * This component owns the remaining view-specific hooks for focused VTMS
 * workflows (camera, radio, session, drivers) and threads them to Shell.
 */
function AppContent({
  config,
  saveConfig,
  resetToDefaults,
}: {
  config: ReturnType<typeof useConfig>["config"];
  saveConfig: ReturnType<typeof useConfig>["saveConfig"];
  resetToDefaults: ReturnType<typeof useConfig>["resetToDefaults"];
}) {
  const { selectedTrackId } = useActiveEvent();

  const { metrics, gps, trail, dtcs, connectionStatus } =
    useTelemetryContext();
  const { alerts, toasts, acknowledgeAlert, dismissToast, clearAlerts, raise, muted, setMuted } =
    useAlertContext();

  const { route } = useRoute();
  const { theme } = useThemeContext();
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [radioDrawerOpen, setRadioDrawerOpen] = useState(false);

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;
  // Both drawers anchor to the right edge; only one may be open at a time.
  const toggleAlertDrawer = useCallback(() => {
    setRadioDrawerOpen(false);
    setAlertDrawerOpen((prev) => !prev);
  }, []);
  const toggleRadioDrawer = useCallback(() => {
    setAlertDrawerOpen(false);
    setRadioDrawerOpen((prev) => !prev);
  }, []);

  // Drawers are contextual to the view they were opened on.
  useEffect(() => {
    setAlertDrawerOpen(false);
    setRadioDrawerOpen(false);
  }, [route.view]);

  const radioTranscripts = useRadioTranscripts(config.mqtt.topicPrefix);
  const radio = useRadioCapture({
    serverUrl: API_BASE,
    onTranscript: radioTranscripts.addLocal,
  });
  const sim = useSimMode(config.mqtt.topicPrefix);
  const drivers = useDrivers();
  const session = useSession();
  const activeSessionId = session.activeSession?.id ?? null;
  const refuels = useRefuels(activeSessionId);
  const targetStintMinutes = config.targetStintMinutes ?? DEFAULT_TARGET_STINT_MINUTES;
  const stint = useStintClock(session.activeSession, refuels, targetStintMinutes);
  useStintTargetAlert(stint, targetStintMinutes, raise);
  const toggleAlertsMuted = useCallback(() => setMuted(!muted), [muted, setMuted]);

  const lapTimer = useLapTimerContext();
  const raceChrono = useRaceChronoLapTiming(config.mqtt.topicPrefix);
  const fuel = useFuelProjection(metrics, effectiveLapCount(raceChrono.lapNumber, lapTimer.laps.length, lapTimer.finishLineConfigured), raceChrono.lapTimesMs);

  const sampleChannels = config.analysis?.sampleChannels ?? [
    "RPM", "SPEED", "COOLANT_TEMP", "OIL_TEMP", "OIL_PRESS", "VOLTAGE", "TPS",
  ];
  useTelemetrySampler(
    metrics,
    activeSessionId,
    null,
    sampleChannels,
  );

  const hasGpsFix = gps.latitude !== null && gps.longitude !== null;
  const telemetryStale = useTelemetryStaleness(
    metrics,
    config.gauges,
    config.mqtt.topicPrefix,
  );

  const simBanner = <SimBanner active={sim.active} info={sim.info} />;

  const statusBarElement = (
    <ErrorBoundary name="Status bar">
      <StatusBar
        connectionStatus={connectionStatus}
        hasGpsFix={hasGpsFix}
        dtcs={dtcs}
        radioState={radio.state}
        radioVisible={radioDrawerOpen}
        alertCount={unacknowledgedCount}
        telemetryStale={telemetryStale}
        stint={stint}
        alertsMuted={muted}
        onToggleAlertDrawer={toggleAlertDrawer}
        onToggleRadio={toggleRadioDrawer}
        onToggleAlertsMuted={toggleAlertsMuted}
      />
    </ErrorBoundary>
  );

  return (
    <React.Fragment key={theme}>
      <Shell
        statusBar={statusBarElement}
        map={
          <ErrorBoundary name="Map">
            <MapView gps={gps} trail={trail} fallbackTrackId={selectedTrackId} />
          </ErrorBoundary>
        }
        gauges={
          <ErrorBoundary name="Gauges">
            <GaugePanel
              metrics={metrics}
              gauges={config.gauges}
              topicPrefix={config.mqtt.topicPrefix}
              chips={<FuelChip fuel={fuel} />}
            />
          </ErrorBoundary>
        }
        radioDrawer={
          <ErrorBoundary name="Radio drawer">
            <RadioDrawer open={radioDrawerOpen} onClose={toggleRadioDrawer}>
              <RadioPanel
                shortcutsEnabled={radioDrawerOpen}
                capture={radio}
                transcripts={radioTranscripts.transcripts}
                mqttStatus={radioTranscripts.connectionStatus}
                onClearTranscripts={radioTranscripts.clear}
              />
            </RadioDrawer>
          </ErrorBoundary>
        }
        cameraPanel={
          <ErrorBoundary name="Cameras">
            <CameraPanel streamBase={CAMERA_STREAM_BASE} />
          </ErrorBoundary>
        }
        connectionBanner={
          <>
            <ConnectionBanner connectionStatus={connectionStatus} telemetryStale={telemetryStale} />
            {simBanner}
          </>
        }
        simBanner={simBanner}
        alertStrip={
          <ErrorBoundary name="Alerts">
            <AlertStrip
              toasts={toasts}
              onDismiss={dismissToast}
              onOpenDrawer={() => setAlertDrawerOpen(true)}
            />
          </ErrorBoundary>
        }
        alertDrawer={
          <ErrorBoundary name="Alert history">
            <AlertDrawer
              open={alertDrawerOpen}
              alerts={alerts}
              onAcknowledge={acknowledgeAlert}
              onClear={clearAlerts}
              onClose={() => setAlertDrawerOpen(false)}
            />
          </ErrorBoundary>
        }
        pitBoardAlerts={alerts}
        pitBoardSessionId={activeSessionId}
        pitBoardStint={stint}
        pitBoardDriverName={session.activeSession?.driver_name ?? null}
        sessionHook={session}
        drivers={drivers.drivers}
        config={config}
        onSaveConfig={saveConfig}
        onResetConfig={resetToDefaults}
        onCreateDriver={drivers.createDriver}
        onDeleteDriver={drivers.deleteDriver}
      />
    </React.Fragment>
  );
}

/**
 * Sets up contexts (telemetry, lap timer, alerts) and renders AppContent.
 */
function AppProviders({
  config,
  saveConfig,
  resetToDefaults,
}: {
  config: ReturnType<typeof useConfig>["config"];
  saveConfig: ReturnType<typeof useConfig>["saveConfig"];
  resetToDefaults: ReturnType<typeof useConfig>["resetToDefaults"];
}) {
  const finishLine = config.finishLine ?? null;
  const sectorLines = config.sectorLines ?? [];

  return (
    <ActiveEventProvider>
      <RouterProvider>
        <TelemetryProvider
          topicPrefix={config.mqtt.topicPrefix}
          calibration={config.calibration}
        >
          <LapTimerProvider
            finishLine={finishLine}
            sectorLines={sectorLines}
          >
              <AlertProvider
                rules={config.alerts ?? []}
                topicPrefix={config.mqtt.topicPrefix}
              >
                <AppContent
                  config={config}
                  saveConfig={saveConfig}
                  resetToDefaults={resetToDefaults}
                />
              </AlertProvider>
            </LapTimerProvider>
          </TelemetryProvider>
      </RouterProvider>
    </ActiveEventProvider>
  );
}

function App() {
  const { config, saveConfig, resetToDefaults } = useConfig();

  return (
    <ThemeProvider>
      <MqttProvider
        brokerUrl={config.mqtt.url}
        topicPrefix={config.mqtt.topicPrefix}
      >
        <AppProviders
          config={config}
          saveConfig={saveConfig}
          resetToDefaults={resetToDefaults}
        />
      </MqttProvider>
    </ThemeProvider>
  );
}

export default App;
